<script setup lang="ts">
import { gsap } from 'gsap'

const props = defineProps<{ searchQuery?: string }>()

const { t, tm, rt } = useI18n()
const localePath = useLocalePath()

type Category = 'all' | 'getting_started' | 'pricing' | 'account'

const activeCategory = ref<Category>('all')
const openIndex = ref<number | null>(0)

const categories: { key: Category }[] = [
  { key: 'all' },
  { key: 'getting_started' },
  { key: 'pricing' },
  { key: 'account' },
]

const allItems = computed(() => {
  const cats = ['getting_started', 'pricing', 'account'] as const
  return cats.flatMap((cat) => {
    const items = tm(`page.faq.items.${cat}`) as Array<{ q: unknown; a: unknown }>
    return items.map(item => ({
      q: rt(item.q),
      a: rt(item.a),
      category: cat as string,
    }))
  })
})

const visibleItems = computed(() => {
  let items = activeCategory.value === 'all'
    ? allItems.value
    : allItems.value.filter(i => i.category === activeCategory.value)

  const q = props.searchQuery?.trim().toLowerCase()
  if (q) {
    items = items.filter(i =>
      i.q.toLowerCase().includes(q) || i.a.toLowerCase().includes(q),
    )
  }
  return items
})

watch([activeCategory, () => props.searchQuery], () => {
  openIndex.value = null
})

function onEnter(el: Element, done: () => void) {
  const target = el as HTMLElement
  target.style.overflow = 'hidden'
  gsap.fromTo(
    target,
    { height: 0, opacity: 0 },
    {
      height: target.scrollHeight,
      opacity: 1,
      duration: 0.35,
      ease: 'power2.out',
      onComplete: () => {
        target.style.height = ''
        target.style.overflow = ''
        done()
      },
    },
  )
}

function onLeave(el: Element, done: () => void) {
  const target = el as HTMLElement
  target.style.overflow = 'hidden'
  gsap.fromTo(
    target,
    { height: target.scrollHeight, opacity: 1 },
    { height: 0, opacity: 0, duration: 0.3, ease: 'power2.in', onComplete: done },
  )
}

const section = ref<HTMLElement | null>(null)

onMounted(() => {
  const el = section.value
  if (!el) return

  gsap.fromTo(
    el.querySelectorAll('.faq-sec-tab'),
    { opacity: 0, y: -16 },
    {
      opacity: 1, y: 0, duration: 0.5, stagger: 0.08, ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 85%', once: true },
    },
  )

  gsap.fromTo(
    el.querySelectorAll('.faq-sec-item'),
    { opacity: 0, y: 24 },
    {
      opacity: 1, y: 0, duration: 0.5, stagger: 0.09, ease: 'power2.out',
      scrollTrigger: { trigger: el.querySelector('.faq-sec-list'), start: 'top 88%', once: true },
    },
  )
})
</script>

<template>
  <section ref="section" class="py-14 md:py-20 bg-amber-50 relative overflow-hidden">
    <div class="container flex flex-col items-center">

      <!-- Category tabs -->
      <div class="flex flex-wrap justify-center gap-3 mb-10 md:mb-12">
        <button
          v-for="cat in categories"
          :key="cat.key"
          class="faq-sec-tab px-5 py-2.5 text-sm md:text-base font-bold border-2 border-gray-900 rounded-lg bg-white cursor-pointer transition-all duration-150"
          :class="activeCategory === cat.key
            ? 'shadow-[4px_4px_0_#111827] -translate-x-0.5 -translate-y-0.5'
            : 'shadow-none hover:shadow-[4px_4px_0_#111827] hover:-translate-x-0.5 hover:-translate-y-0.5'"
          @click="activeCategory = cat.key">
          {{ t(`page.faq.categories.${cat.key}`) }}
        </button>
      </div>

      <!-- Accordion list -->
      <div class="faq-sec-list w-full max-w-2xl flex flex-col gap-4 mb-12">
        <p v-if="visibleItems.length === 0" class="text-center text-gray-500 py-6">
          {{ t('page.faq.no_results') }}
        </p>

        <div
          v-for="(item, index) in visibleItems"
          :key="`${activeCategory}-${index}`"
          class="faq-sec-item bg-white border-2 border-gray-900 rounded-lg shadow-[6px_6px_0_#111827] overflow-hidden">
          <button
            class="flex items-center justify-between gap-6 px-6 py-5 text-start font-bold text-gray-900 cursor-pointer bg-transparent border-none w-full transition-colors hover:bg-gray-50 text-base md:text-lg"
            :aria-expanded="openIndex === index"
            @click="openIndex = openIndex === index ? null : index">
            <span>{{ item.q }}</span>
            <span
              class="size-8 min-w-8 shrink-0 flex items-center justify-center relative transition-transform duration-[450ms] ease-[cubic-bezier(0.65,0,0.35,1)]"
              :class="openIndex === index ? 'rotate-180' : 'rotate-0'">
              <span class="absolute w-[16px] h-[2.5px] bg-gray-900 rounded-full" />
              <span
                class="absolute w-[16px] h-[2.5px] bg-gray-900 rounded-full origin-center transition-transform duration-[450ms] ease-[cubic-bezier(0.65,0,0.35,1)]"
                :class="openIndex === index ? 'rotate-0' : 'rotate-90'" />
            </span>
          </button>

          <Transition :css="false" @enter="onEnter" @leave="onLeave">
            <div v-if="openIndex === index" class="px-6 pb-5 text-gray-700 leading-[1.8] text-sm md:text-base font-medium">
              {{ item.a }}
            </div>
          </Transition>
        </div>
      </div>

      <!-- Footer CTA -->
      <div class="text-center">
        <p class="text-gray-500 font-medium mb-4">{{ t('page.faq.still_have_question') }}</p>
        <NuxtLink
          v-magnetic
          :to="localePath('/contact-sales')"
          class="bfm-btn bfm-btn-md bg-sky-blue text-white border-gray-900 shadow-[4px_4px_0_#111827] hover:shadow-[6px_6px_0_#111827]">
          {{ t('page.faq.cta') }}
        </NuxtLink>
      </div>
    </div>
  </section>
</template>
